import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Building2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import AssetParametersSection from './AssetParametersSection';
import DepreciationMethodSection from './DepreciationMethodSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import TaxAccountingSection from './TaxAccountingSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета амортизации
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  assetTypes,
  depreciationGroups,
  saveAsset 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveAssetName, setSaveAssetName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем группу амортизации при изменении типа актива
      if (name === 'assetType') {
        const selectedAssetType = assetTypes.find(type => type.id === fieldValue);
        if (selectedAssetType) {
          newState.assetGroup = selectedAssetType.defaultGroup;
        }
      }

      // Обновляем коэффициент при изменении связанных полей
      if (name === 'includeAccelerationCoefficient' && !checked) {
        newState.accelerationCoefficient = '1';
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeRevaluation' && !checked) {
        newState.revaluationAmount = '0';
        newState.revaluationYear = '3';
        newState.revaluationType = 'increase';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [assetTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      assetName, assetCost, salvageValue, usefulLife, startDate,
      depreciationMethod, assetType, assetGroup, specialCoefficient,
      includeAccelerationCoefficient, accelerationCoefficient,
      productionUnits, firstYearUnits,
      includeRevaluation, revaluationAmount, revaluationYear, revaluationType
    } = formData;

    // Парсинг числовых значений
    const numAssetCost = parseFloatOrDefault(assetCost, -1);
    const numSalvageValue = parseFloatOrDefault(salvageValue, -1);
    const numUsefulLife = parseFloatOrDefault(usefulLife, -1);
    const numSpecialCoefficient = parseFloatOrDefault(specialCoefficient, 1);
    
    // Валидация базовых полей
    if (!assetName || assetName.trim() === '') newErrors.assetName = 'Введите название актива.';
    
    if (numAssetCost <= 0) newErrors.assetCost = 'Стоимость должна быть положительной.';
    if (!newErrors.assetCost && numAssetCost > 1000000000) newErrors.assetCost = 'Максимальная стоимость 1 млрд ₽.';
    
    if (numSalvageValue < 0) newErrors.salvageValue = 'Ликвидационная стоимость не может быть отрицательной.';
    if (!newErrors.salvageValue && numSalvageValue >= numAssetCost) {
      newErrors.salvageValue = 'Ликвидационная стоимость не может быть больше или равна первоначальной.';
    }
    
    if (numUsefulLife <= 0) newErrors.usefulLife = 'Срок должен быть положительным.';
    if (!newErrors.usefulLife && numUsefulLife > 100) newErrors.usefulLife = 'Максимальный срок 100 лет.';
    
    // Проверка даты начала
    if (!startDate) newErrors.startDate = 'Укажите дату начала эксплуатации.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация метода амортизации
    if (!depreciationMethod || !['straight_line', 'reducing_balance', 'sum_of_years', 'units_of_production'].includes(depreciationMethod)) {
      newErrors.depreciationMethod = "Выберите корректный метод амортизации.";
    }

    // Если выбран метод единиц продукции, проверяем дополнительные поля
    if (depreciationMethod === 'units_of_production') {
      const numProductionUnits = parseFloatOrDefault(productionUnits, -1);
      const numFirstYearUnits = parseFloatOrDefault(firstYearUnits, -1);
      
      if (numProductionUnits <= 0) newErrors.productionUnits = 'Количество должно быть положительным.';
      if (numFirstYearUnits < 0) newErrors.firstYearUnits = 'Количество не может быть отрицательным.';
      if (!newErrors.firstYearUnits && !newErrors.productionUnits && numFirstYearUnits > numProductionUnits) {
        newErrors.firstYearUnits = 'Количество за первый год не может быть больше общего количества.';
      }
    }

    // Валидация специального коэффициента
    if (numSpecialCoefficient <= 0 || numSpecialCoefficient > 3) {
      newErrors.specialCoefficient = 'Специальный коэффициент должен быть от 1 до 3.';
    }

    // Валидация для ускоренной амортизации
    if (includeAccelerationCoefficient) {
      const numAccelerationCoefficient = parseFloatOrDefault(accelerationCoefficient, -1);
      if (numAccelerationCoefficient <= 0 || numAccelerationCoefficient > 3) {
        newErrors.accelerationCoefficient = 'Коэффициент должен быть от 1 до 3.';
      }
    }

    // Валидация для переоценки
    if (includeRevaluation) {
      const numRevaluationAmount = parseFloatOrDefault(revaluationAmount, -1);
      const numRevaluationYear = parseIntOrDefault(revaluationYear, -1);
      
      if (numRevaluationAmount <= 0) {
        newErrors.revaluationAmount = 'Сумма должна быть положительной.';
      }
      
      if (!isInteger(revaluationYear) || numRevaluationYear <= 0) {
        newErrors.revaluationYear = 'Введите целый положительный год.';
      }
      
      if (!newErrors.usefulLife && !newErrors.revaluationYear && numRevaluationYear > numUsefulLife) {
        newErrors.revaluationYear = `Год переоценки (${numRevaluationYear}) не может быть позже срока службы (${numUsefulLife} лет).`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета линейной амортизации
  const calculateStraightLineDepreciation = (cost, salvage, life) => {
    return (cost - salvage) / life;
  };

  // Функция расчета амортизации методом уменьшаемого остатка
  const calculateReducingBalanceDepreciation = (cost, salvage, life, year, balance, rate) => {
    const straightLineRate = 1 / life;
    const reducingRate = straightLineRate * rate;
    
    const depreciation = balance * reducingRate;
    
    // Проверка на последний год
    const remainingYears = life - year + 1;
    const straightLineAmount = (balance - salvage) / remainingYears;
    
    // В последний год или если по прямолинейному методу списание будет больше
    if (year === life || straightLineAmount > depreciation) {
      return Math.max(0, balance - salvage);
    }
    
    return depreciation;
  };

  // Функция расчета амортизации по сумме чисел лет
  const calculateSumOfYearsDepreciation = (cost, salvage, life, year) => {
    const sumOfYears = (life * (life + 1)) / 2;
    const remainingYears = life - year + 1;
    
    return ((cost - salvage) * remainingYears) / sumOfYears;
  };

  // Функция расчета амортизации пропорционально объему продукции
  const calculateUnitsOfProductionDepreciation = (cost, salvage, totalUnits, unitsThisYear) => {
    return (cost - salvage) * (unitsThisYear / totalUnits);
  };

  // Функция расчета налоговой амортизации (линейный метод)
  const calculateTaxLinearDepreciation = (cost, life) => {
    // Норма амортизации в месяц для линейного метода
    const monthlyRate = 1 / (life * 12);
    return cost * monthlyRate;
  };

  // Функция расчета налоговой амортизации (нелинейный метод)
  const calculateTaxNonLinearDepreciation = (balance, group) => {
    // Законодательно установленные нормы для нелинейного метода по группам
    const rates = {
      '1': 14.3, '2': 8.8, '3': 5.6, '4': 3.8, '5': 2.7, 
      '6': 1.8, '7': 1.3, '8': 1.0, '9': 0.8, '10': 0.7
    };
    
    const monthlyRate = rates[group] / 100;
    return balance * monthlyRate;
  };

  // Функция расчета графика амортизации
  const calculateDepreciationSchedule = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const assetName = formData.assetName;
        const numAssetCost = parseFloatOrDefault(formData.assetCost);
        const numSalvageValue = parseFloatOrDefault(formData.salvageValue);
        const numUsefulLife = parseFloatOrDefault(formData.usefulLife);
        const depreciationMethod = formData.depreciationMethod;
        const startDateObj = new Date(formData.startDate);
        const assetType = formData.assetType;
        const assetGroup = formData.assetGroup;
        const taxMethod = formData.taxMethod;
        const includeTaxCalculation = formData.includeTaxCalculation;
        
        const specialCoefficient = parseFloatOrDefault(formData.specialCoefficient, 1);
        const includeAccelerationCoefficient = formData.includeAccelerationCoefficient;
        const accelerationCoefficient = includeAccelerationCoefficient ? 
            parseFloatOrDefault(formData.accelerationCoefficient, 1) : 1;
        
        const includeMonthlySplit = formData.includeMonthlySplit;
        
        const includeRevaluation = formData.includeRevaluation;
        const revaluationAmount = includeRevaluation ? parseFloatOrDefault(formData.revaluationAmount, 0) : 0;
        const revaluationYear = includeRevaluation ? parseIntOrDefault(formData.revaluationYear, 0) : 0;
        const revaluationType = formData.revaluationType;

        // Параметры для метода единиц продукции
        const productionUnits = parseFloatOrDefault(formData.productionUnits, 0);
        const firstYearUnits = parseFloatOrDefault(formData.firstYearUnits, 0);

        // --- Инициализируем переменные для расчета ---
        let bookValue = numAssetCost;
        let accumulatedDepreciation = 0;
        let assetWasRevalued = false;
        
        // Инициализация для налогового учета
        let taxBookValue = numAssetCost;
        let taxAccumulatedDepreciation = 0;
        
        // Для нелинейного метода - остаток амортизационной группы
        let taxGroupBalance = numAssetCost;
        
        // Коэффициент ускоренной амортизации (общий)
        const totalCoefficient = specialCoefficient * accelerationCoefficient;

        const depreciationSchedule = [];
        
        // Расчет количества единиц продукции по годам (для соответствующего метода)
        let remainingUnits = productionUnits - firstYearUnits;
        let unitsPerYear = [];
        
        if (depreciationMethod === 'units_of_production') {
          unitsPerYear[1] = firstYearUnits; // Единицы для первого года указаны пользователем
          
          // Для остальных лет распределяем равномерно
          const remainingYears = numUsefulLife - 1;
          const averageUnitsPerYear = remainingYears > 0 ? remainingUnits / remainingYears : 0;
          
          for (let year = 2; year <= numUsefulLife; year++) {
            unitsPerYear[year] = Math.min(remainingUnits, averageUnitsPerYear);
            remainingUnits -= unitsPerYear[year];
          }
        }

        // --- Цикл расчета графика амортизации ---
        for (let year = 1; year <= numUsefulLife; year++) {
          // Начальная стоимость актива в этом году
          const startingBookValue = bookValue;
          let annualDepreciation = 0;
          let monthlyDepreciation = 0;
          
          // Налоговый учет
          let taxAnnualDepreciation = 0;
          let monthlyTaxData = [];

          // Применяем переоценку, если это соответствующий год
          if (includeRevaluation && year === revaluationYear && !assetWasRevalued) {
            if (revaluationType === 'increase') {
              // Увеличиваем стоимость
              bookValue += revaluationAmount;
              // Корректируем накопленную амортизацию в той же пропорции
              const revaluationRatio = (bookValue / (bookValue - revaluationAmount));
              accumulatedDepreciation *= revaluationRatio;
            } else {
              // Уменьшаем стоимость
              bookValue = Math.max(numSalvageValue, bookValue - revaluationAmount);
              // Корректируем накопленную амортизацию в той же пропорции
              const revaluationRatio = (bookValue / (bookValue + revaluationAmount));
              accumulatedDepreciation *= revaluationRatio;
            }
            assetWasRevalued = true;
          }

          // Расчет годовой амортизации в зависимости от метода
          switch (depreciationMethod) {
            case 'straight_line':
              // Линейный метод
              annualDepreciation = calculateStraightLineDepreciation(
                numAssetCost, 
                numSalvageValue, 
                numUsefulLife
              ) * totalCoefficient;
              break;
              
            case 'reducing_balance':
              // Метод уменьшаемого остатка
              annualDepreciation = calculateReducingBalanceDepreciation(
                numAssetCost, 
                numSalvageValue, 
                numUsefulLife, 
                year, 
                startingBookValue, 
                totalCoefficient * 2 // Коэффициент ускорения для метода уменьшаемого остатка
              );
              break;
              
            case 'sum_of_years':
              // Метод по сумме чисел лет срока полезного использования
              annualDepreciation = calculateSumOfYearsDepreciation(
                numAssetCost, 
                numSalvageValue, 
                numUsefulLife, 
                year
              ) * totalCoefficient;
              break;
              
            case 'units_of_production':
              // Метод пропорционально объему продукции
              annualDepreciation = calculateUnitsOfProductionDepreciation(
                numAssetCost, 
                numSalvageValue, 
                productionUnits, 
                unitsPerYear[year]
              );
              break;
              
            default:
              annualDepreciation = 0;
          }

          // Ограничиваем амортизацию, чтобы не уйти ниже ликвидационной стоимости
          annualDepreciation = Math.min(annualDepreciation, startingBookValue - numSalvageValue);
          annualDepreciation = Math.max(0, annualDepreciation);
          
          // Обновляем балансовую стоимость и накопленную амортизацию
          bookValue -= annualDepreciation;
          accumulatedDepreciation += annualDepreciation;
          
          // Расчет ежемесячной амортизации для бухгалтерского учета
          monthlyDepreciation = annualDepreciation / 12;

          // Расчет для налогового учета
          if (includeTaxCalculation) {
            if (taxMethod === 'linear') {
              // Линейный метод налогового учета
              taxAnnualDepreciation = calculateTaxLinearDepreciation(numAssetCost, numUsefulLife) * 12;
            } else {
              // Нелинейный метод налогового учета
              // Если остаток меньше 20% от первоначальной стоимости, списываем его равными долями
              if (taxGroupBalance <= numAssetCost * 0.2) {
                taxAnnualDepreciation = taxGroupBalance / Math.max(1, numUsefulLife - year + 1);
                taxGroupBalance -= taxAnnualDepreciation;
              } else {
                let monthlyTaxDepreciation = 0;
                let monthlyBalance = taxGroupBalance;
                
                for (let month = 1; month <= 12; month++) {
                  const monthDepreciation = calculateTaxNonLinearDepreciation(monthlyBalance, assetGroup);
                  monthlyTaxDepreciation += monthDepreciation;
                  monthlyBalance -= monthDepreciation;
                  
                  if (includeMonthlySplit) {
                    monthlyTaxData.push({
                      month,
                      depreciation: monthDepreciation,
                      balance: monthlyBalance
                    });
                  }
                }
                
                taxAnnualDepreciation = monthlyTaxDepreciation;
                taxGroupBalance = monthlyBalance;
              }
            }
            
            taxAccumulatedDepreciation += taxAnnualDepreciation;
            taxBookValue = numAssetCost - taxAccumulatedDepreciation;
          }

          // Расчет даты
          const currentYear = startDateObj.getFullYear() + year - 1;
          
          // Добавляем запись в график амортизации
          depreciationSchedule.push({
            year,
            date: currentYear,
            startingBookValue,
            depreciation: annualDepreciation,
            accumulatedDepreciation,
            endingBookValue: bookValue,
            monthlyDepreciation,
            taxDepreciation: taxAnnualDepreciation,
            taxBookValue,
            taxAccumulatedDepreciation,
            monthlyTaxData: includeMonthlySplit ? monthlyTaxData : null,
            unitsProduced: depreciationMethod === 'units_of_production' ? unitsPerYear[year] : null
          });
        }

        // --- Готовим итоговые результаты ---
        const totalDepreciation = depreciationSchedule.reduce((sum, item) => sum + item.depreciation, 0);
        const totalTaxDepreciation = includeTaxCalculation 
          ? depreciationSchedule.reduce((sum, item) => sum + item.taxDepreciation, 0) 
          : 0;
        
        // Разница между бухгалтерским и налоговым учетом
        const accountingTaxDifference = includeTaxCalculation 
          ? totalDepreciation - totalTaxDepreciation 
          : 0;
        
        // Расчет средней годовой нормы амортизации
        const averageDepreciationRate = (totalDepreciation / numAssetCost) * (100 / numUsefulLife);
        
        const finalResults = {
          assetName,
          assetCost: numAssetCost,
          salvageValue: numSalvageValue,
          depreciableAmount: numAssetCost - numSalvageValue,
          usefulLife: numUsefulLife,
          depreciationMethod,
          
          totalDepreciation,
          averageAnnualDepreciation: totalDepreciation / numUsefulLife,
          averageMonthlyDepreciation: totalDepreciation / (numUsefulLife * 12),
          averageDepreciationRate,
          
          assetType,
          assetGroup,
          
          // Налоговый учет
          taxMethod,
          totalTaxDepreciation,
          accountingTaxDifference,
          
          // Данные по переоценке
          wasRevalued: assetWasRevalued,
          revaluationAmount: assetWasRevalued ? revaluationAmount : 0,
          revaluationYear: assetWasRevalued ? revaluationYear : 0,
          
          // Данные для метода единиц продукции
          productionUnits: depreciationMethod === 'units_of_production' ? productionUnits : null,
          
          startDate: startDateObj,
          
          // Краткая версия графика для предпросмотра (первые 5 лет)
          scheduleShort: depreciationSchedule.slice(0, 5)
        };

        setResults(finalResults);
        setSchedule(depreciationSchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      assetName: 'Новое оборудование',
      assetCost: '1000000',
      salvageValue: '100000',
      usefulLife: '10',
      depreciationMethod: 'straight_line',
      startDate: new Date().toISOString().split('T')[0],
      assetType: 'equipment',
      assetGroup: '3',
      taxMethod: 'linear',
      specialCoefficient: '1',
      includeTaxCalculation: true,
      includeAccelerationCoefficient: false,
      accelerationCoefficient: '2',
      productionUnits: '100000',
      firstYearUnits: '10000',
      includeMonthlySplit: false,
      includeRevaluation: false,
      revaluationAmount: '0',
      revaluationYear: '3',
      revaluationType: 'increase',
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveAssetName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика амортизации в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Год', 'Балансовая стоимость на начало', 'Амортизация за год', 'Накопленная амортизация', 'Балансовая стоимость на конец'];
    
    if (results.taxMethod && results.includeTaxCalculation) {
      headers.push('Налоговая амортизация', 'Налоговая остаточная стоимость');
    }
    
    if (formData.depreciationMethod === 'units_of_production') {
      headers.push('Единиц произведено');
    }

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      const rowData = [
        item.year,
        formatCsvNumber(item.startingBookValue),
        formatCsvNumber(item.depreciation),
        formatCsvNumber(item.accumulatedDepreciation),
        formatCsvNumber(item.endingBookValue),
      ];
      
      if (results.taxMethod && results.includeTaxCalculation) {
        rowData.push(
          formatCsvNumber(item.taxDepreciation),
          formatCsvNumber(item.taxBookValue)
        );
      }
      
      if (formData.depreciationMethod === 'units_of_production') {
        rowData.push(item.unitsProduced || 0);
      }
      
      return rowData.join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      'ИТОГО:', 
      formatCsvNumber(results.assetCost),
      formatCsvNumber(results.totalDepreciation),
      formatCsvNumber(results.totalDepreciation),
      formatCsvNumber(results.salvageValue)
    ];
    
    if (results.taxMethod && results.includeTaxCalculation) {
      totalRow.push(
        formatCsvNumber(results.totalTaxDepreciation),
        formatCsvNumber(results.assetCost - results.totalTaxDepreciation)
      );
    }
    
    if (formData.depreciationMethod === 'units_of_production') {
      totalRow.push(formatCsvNumber(results.productionUnits || 0));
    }
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Название актива:', results.assetName],
      ['Первоначальная стоимость:', formatCsvNumber(results.assetCost) + ' ₽'],
      ['Ликвидационная стоимость:', formatCsvNumber(results.salvageValue) + ' ₽'],
      ['Срок полезного использования:', `${results.usefulLife} лет`],
      ['Метод амортизации:', getDepreciationMethodName(formData.depreciationMethod)],
      ['Дата начала эксплуатации:', new Date(formData.startDate).toLocaleDateString('ru-RU')],
      ['Средняя годовая норма амортизации:', formatCsvNumber(results.averageDepreciationRate) + '%'],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'график_амортизации.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Вспомогательная функция для получения названия метода амортизации
  const getDepreciationMethodName = (method) => {
    const methodNames = {
      'straight_line': 'Линейный метод',
      'reducing_balance': 'Метод уменьшаемого остатка',
      'sum_of_years': 'По сумме чисел лет',
      'units_of_production': 'Пропорционально объему продукции'
    };
    return methodNames[method] || method;
  };

  // Обработчик сохранения расчета
  const handleSaveAsset = () => {
    if (showSaveDialog) {
      const savedResult = saveAsset(saveAssetName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveAssetName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateDepreciationSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-6 h-6 text-indigo-500" />
          Параметры расчета амортизации
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры актива */}
        <AssetParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          assetTypes={assetTypes}
          depreciationGroups={depreciationGroups}
        />

        {/* Методы амортизации */}
        <DepreciationMethodSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные параметры */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Налоговый учет */}
        <TaxAccountingSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveAssetName}
                onChange={(e) => setSaveAssetName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveAsset}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать амортизацию'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveAsset}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;